// 遊戲日誌面板 - Game Log Panel
// 顯示戰鬥日誌、效果觸發記錄、傷害數值

import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useGameStore } from '../../store';
import type { GameEvent, GameEventType } from '../../types/events';

/**
 * 安全取得字串值
 */
function str(val: unknown, fallback = '???'): string {
  if (val == null) return fallback;
  if (typeof val === 'string') return val;
  if (typeof val === 'number') return String(val);
  return fallback;
}

/**
 * 事件類型對應的顯示設定
 */
interface EventDisplay {
  icon: string;
  color: string;
  format: (event: GameEvent) => string;
}

const EVENT_DISPLAY: Partial<Record<GameEventType, EventDisplay>> = {
  GAME_START: {
    icon: '🎮',
    color: '#88ff88',
    format: () => '遊戲開始',
  },
  TURN_START: {
    icon: '🔄',
    color: '#88ccff',
    format: (e) => {
      const p = e.payload as Record<string, unknown>;
      return `第 ${String(e.turnNumber)} 回合 — ${str(p.activePlayerName, str(e.playerId))} 的回合`;
    },
  },
  PHASE_CHANGE: {
    icon: '▶',
    color: '#aaaaaa',
    format: (e) => {
      const p = e.payload as Record<string, unknown>;
      const phaseNames: Record<string, string> = {
        draw: '抽牌階段',
        defense: '防守階段',
        summon: '召喚階段',
        tactics: '戰術階段',
        end: '結束階段',
      };
      const newPhase = phaseNames[p.newPhase as string] ?? str(p.newPhase);
      return `→ ${newPhase}`;
    },
  },
  CARD_DRAW: {
    icon: '🃏',
    color: '#cccccc',
    format: (e) => {
      const p = e.payload as Record<string, unknown>;
      return `抽牌: ${str(p.cardName, '???')}`;
    },
  },
  CARD_SUMMON: {
    icon: '⭐',
    color: '#ffdd44',
    format: (e) => {
      const p = e.payload as Record<string, unknown>;
      const tactical = p.isTactical ? ' (戰術部署)' : '';
      return `召喚 ${str(p.cardName, '???')}${tactical}`;
    },
  },
  ATTACK_DECLARE: {
    icon: '⚔️',
    color: '#ff8844',
    format: (e) => {
      const p = e.payload as Record<string, unknown>;
      return `宣告攻擊: ${str(p.cardName, '攻擊')}`;
    },
  },
  COMBAT_START: {
    icon: '💥',
    color: '#ff6644',
    format: (e) => {
      const p = e.payload as Record<string, unknown>;
      const attackers = p.attackers as Array<Record<string, unknown>> | undefined;
      const defenders = p.defenders as Array<Record<string, unknown>> | undefined;
      const atk = str(attackers?.[0]?.cardName);
      const def = str(defenders?.[0]?.cardName, '(空位)');
      return `戰鬥: ${atk} vs ${def}`;
    },
  },
  COMBAT_RESULT: {
    icon: '🏆',
    color: '#ffaa44',
    format: (e) => {
      const p = e.payload as Record<string, unknown>;
      return `勝者: ${str(p.winnerName)}`;
    },
  },
  UNIT_DESTROYED: {
    icon: '💀',
    color: '#ff4444',
    format: (e) => {
      const p = e.payload as Record<string, unknown>;
      const by = p.sourceCardName ? ` (被 ${str(p.sourceCardName)} 擊破)` : '';
      return `${str(p.cardName)} 被破壞${by}`;
    },
  },
  SPELL_CAST: {
    icon: '✨',
    color: '#dd88ff',
    format: (e) => {
      const p = e.payload as Record<string, unknown>;
      return `施放 ${str(p.cardName, '???')}`;
    },
  },
  SPELL_RESOLVE: {
    icon: '🔮',
    color: '#bb66dd',
    format: (e) => {
      const p = e.payload as Record<string, unknown>;
      return `法術結算: ${str(p.cardName, '???')}`;
    },
  },
  EFFECT_TRIGGER: {
    icon: '⚡',
    color: '#ffcc00',
    format: (e) => {
      const p = e.payload as Record<string, unknown>;
      const name = str(p.effectName, str(p.sourceCardName, '效果'));
      return `效果觸發: ${name}`;
    },
  },
  EFFECT_RESOLVE: {
    icon: '🔧',
    color: '#ccaa44',
    format: (e) => {
      const p = e.payload as Record<string, unknown>;
      const changes = p.changes as string[] | undefined;
      if (changes && changes.length > 0) {
        return changes.join(', ');
      }
      return `效果結算: ${str(p.effectName, '???')}`;
    },
  },
  FIELD_MAGIC_PLACE: {
    icon: '🌍',
    color: '#44dddd',
    format: (e) => {
      const p = e.payload as Record<string, unknown>;
      return `放置場地魔法: ${str(p.cardName, '???')}`;
    },
  },
  TURN_END: {
    icon: '⏹',
    color: '#888888',
    format: (e) => `回合 ${String(e.turnNumber)} 結束`,
  },
  GAME_END: {
    icon: '🏁',
    color: '#ffd700',
    format: (e) => {
      const p = e.payload as Record<string, unknown>;
      return `遊戲結束 — ${str(p.winnerName, '???')} 獲勝！`;
    },
  },
};

// 要顯示的事件類型（過濾掉太細節的事件）
const VISIBLE_EVENT_TYPES: GameEventType[] = [
  'GAME_START', 'TURN_START', 'PHASE_CHANGE',
  'CARD_DRAW', 'CARD_SUMMON',
  'ATTACK_DECLARE', 'COMBAT_START', 'COMBAT_RESULT', 'UNIT_DESTROYED',
  'SPELL_CAST', 'SPELL_RESOLVE', 'FIELD_MAGIC_PLACE',
  'EFFECT_TRIGGER', 'EFFECT_RESOLVE',
  'TURN_END', 'GAME_END',
];

type LogFilter = 'all' | 'combat' | 'effects' | 'turns';

const FILTER_CONFIG: Record<LogFilter, { label: string; types: GameEventType[] }> = {
  all: { label: '全部', types: VISIBLE_EVENT_TYPES },
  combat: {
    label: '戰鬥',
    types: ['ATTACK_DECLARE', 'COMBAT_START', 'COMBAT_RESULT', 'UNIT_DESTROYED'],
  },
  effects: {
    label: '效果',
    types: ['SPELL_CAST', 'SPELL_RESOLVE', 'FIELD_MAGIC_PLACE', 'EFFECT_TRIGGER', 'EFFECT_RESOLVE'],
  },
  turns: {
    label: '回合',
    types: ['GAME_START', 'TURN_START', 'TURN_END', 'GAME_END'],
  },
};

/**
 * 遊戲日誌面板
 */
export function GameLogPanel() {
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [filter, setFilter] = useState<LogFilter>('all');
  const [autoScroll, setAutoScroll] = useState(true);
  const logRef = useRef<HTMLDivElement>(null);
  const { eventLogger } = useGameStore();

  // 取得事件列表
  const events = useMemo(() => {
    if (!eventLogger) return [];
    const allEvents = eventLogger.getVisibleEvents('player1');
    const filterTypes = FILTER_CONFIG[filter].types;
    return allEvents.filter(e => filterTypes.includes(e.type));
  }, [eventLogger, filter, eventLogger?.count]);

  // 自動捲動到底部
  useEffect(() => {
    if (autoScroll && logRef.current) {
      logRef.current.scrollTop = logRef.current.scrollHeight;
    }
  }, [events.length, autoScroll]);

  const handleScroll = useCallback(() => {
    if (!logRef.current) return;
    const { scrollTop, scrollHeight, clientHeight } = logRef.current;
    // 如果使用者手動往上捲，關閉自動捲動
    setAutoScroll(scrollHeight - scrollTop - clientHeight < 30);
  }, []);

  // 切換按鈕（始終顯示在右下角）
  if (!isOpen) {
    return (
      <button
        style={styles.toggleButton}
        onClick={() => { setIsOpen(true); }}
        title="遊戲日誌"
      >
        📜
      </button>
    );
  }

  if (isMinimized) {
    return (
      <div style={styles.minimizedBar}>
        <span style={styles.minimizedTitle}>📜 日誌 ({String(events.length)})</span>
        <button
          style={styles.iconBtn}
          onClick={() => { setIsMinimized(false); }}
          title="展開"
        >
          ↗
        </button>
        <button
          style={styles.iconBtn}
          onClick={() => { setIsOpen(false); }}
          title="關閉"
        >
          ✕
        </button>
      </div>
    );
  }

  return (
    <div style={styles.panel}>
      {/* 標題列 */}
      <div style={styles.header}>
        <span style={styles.title}>📜 遊戲日誌</span>
        <div style={styles.headerActions}>
          <button
            style={styles.iconBtn}
            onClick={() => { setIsMinimized(true); }}
            title="最小化"
          >
            ↙
          </button>
          <button
            style={styles.iconBtn}
            onClick={() => { setIsOpen(false); }}
            title="關閉"
          >
            ✕
          </button>
        </div>
      </div>

      {/* 篩選列 */}
      <div style={styles.filterBar}>
        {(Object.keys(FILTER_CONFIG) as LogFilter[]).map((f) => (
          <button
            key={f}
            style={{
              ...styles.filterBtn,
              backgroundColor: filter === f ? 'rgba(255,255,255,0.2)' : 'transparent',
              color: filter === f ? '#ffffff' : '#888888',
            }}
            onClick={() => { setFilter(f); }}
          >
            {FILTER_CONFIG[f].label}
          </button>
        ))}
      </div>

      {/* 日誌內容 */}
      <div
        ref={logRef}
        style={styles.logContent}
        onScroll={handleScroll}
      >
        {events.length === 0 && (
          <div style={styles.emptyMsg}>暫無記錄</div>
        )}
        {events.map((event) => {
          const display = EVENT_DISPLAY[event.type];
          if (!display) return null;

          const isTurnStart = event.type === 'TURN_START';

          return (
            <div
              key={event.id}
              style={{
                ...styles.logEntry,
                ...(isTurnStart ? styles.turnStartEntry : {}),
              }}
            >
              <span style={styles.logIcon}>{display.icon}</span>
              <span style={{ ...styles.logText, color: display.color }}>
                {display.format(event)}
              </span>
            </div>
          );
        })}
      </div>

      {/* 底部狀態 */}
      <div style={styles.footer}>
        <span style={styles.footerText}>{String(events.length)} 條記錄</span>
        <button
          style={{
            ...styles.footerBtn,
            color: autoScroll ? '#88ff88' : '#888888',
          }}
          onClick={() => {
            setAutoScroll(true);
            if (logRef.current) {
              logRef.current.scrollTop = logRef.current.scrollHeight;
            }
          }}
        >
          ↓ 自動捲動
        </button>
      </div>
    </div>
  );
}

// ─── Styles ──────────────────────────────────────────────────

const styles: Record<string, React.CSSProperties> = {
  toggleButton: {
    position: 'fixed',
    bottom: '20px',
    right: '20px',
    width: '44px',
    height: '44px',
    borderRadius: '50%',
    border: '2px solid rgba(255,255,255,0.2)',
    backgroundColor: 'rgba(26,26,46,0.9)',
    color: '#ffffff',
    fontSize: '20px',
    cursor: 'pointer',
    zIndex: 1500,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.2s',
  },

  minimizedBar: {
    position: 'fixed',
    bottom: '20px',
    right: '20px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '6px 12px',
    backgroundColor: 'rgba(26,26,46,0.95)',
    border: '1px solid rgba(255,255,255,0.15)',
    borderRadius: '8px',
    zIndex: 1500,
  },

  minimizedTitle: {
    color: '#cccccc',
    fontSize: '13px',
  },

  panel: {
    position: 'fixed',
    bottom: '20px',
    right: '20px',
    width: '340px',
    height: '420px',
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: 'rgba(16,16,32,0.95)',
    border: '1px solid rgba(255,255,255,0.15)',
    borderRadius: '12px',
    overflow: 'hidden',
    zIndex: 1500,
    backdropFilter: 'blur(10px)',
    boxShadow: '0 4px 20px rgba(0,0,0,0.5)',
  },

  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '8px 12px',
    borderBottom: '1px solid rgba(255,255,255,0.1)',
    backgroundColor: 'rgba(255,255,255,0.03)',
  },

  title: {
    color: '#ffffff',
    fontSize: '14px',
    fontWeight: 700,
  },

  headerActions: {
    display: 'flex',
    gap: '4px',
  },

  iconBtn: {
    background: 'none',
    border: 'none',
    color: '#888888',
    fontSize: '16px',
    cursor: 'pointer',
    padding: '2px 6px',
    borderRadius: '4px',
    transition: 'color 0.15s',
  },

  filterBar: {
    display: 'flex',
    gap: '2px',
    padding: '4px 8px',
    borderBottom: '1px solid rgba(255,255,255,0.05)',
  },

  filterBtn: {
    flex: 1,
    padding: '4px 0',
    border: 'none',
    borderRadius: '4px',
    fontSize: '12px',
    cursor: 'pointer',
    transition: 'all 0.15s',
    fontFamily: '"Noto Sans TC", sans-serif',
  },

  logContent: {
    flex: 1,
    overflowY: 'auto',
    padding: '4px 8px',
    scrollbarWidth: 'thin',
    scrollbarColor: 'rgba(255,255,255,0.15) transparent',
  },

  emptyMsg: {
    color: '#666666',
    fontSize: '13px',
    textAlign: 'center',
    padding: '20px',
  },

  logEntry: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '6px',
    padding: '3px 4px',
    borderBottom: '1px solid rgba(255,255,255,0.03)',
    fontSize: '12px',
    lineHeight: 1.4,
  },

  turnStartEntry: {
    marginTop: '4px',
    paddingTop: '6px',
    borderTop: '1px solid rgba(136,204,255,0.2)',
    backgroundColor: 'rgba(136,204,255,0.05)',
    borderRadius: '4px',
  },

  logIcon: {
    fontSize: '12px',
    flexShrink: 0,
    width: '18px',
    textAlign: 'center',
  },

  logText: {
    fontSize: '12px',
    lineHeight: 1.4,
    wordBreak: 'break-word',
  },

  footer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '4px 10px',
    borderTop: '1px solid rgba(255,255,255,0.08)',
    backgroundColor: 'rgba(255,255,255,0.02)',
  },

  footerText: {
    color: '#666666',
    fontSize: '11px',
  },

  footerBtn: {
    background: 'none',
    border: 'none',
    fontSize: '11px',
    cursor: 'pointer',
    padding: '2px 6px',
  },
};
